import memoize from "lodash/memoize";
import type { DoorLoopLeadDto } from "@/dto";
import { calculateLeadScore } from "./leadScoreCalculator";
import type {
  CalendlyCallResponse,
  CalendlyPayload,
  CalendlyResult,
  DLMCCookie,
  HubSpotObject,
  NeverbounceResponse
} from "./types";
import { JobTitlesMarketing, MainPropertyTypeEnum, OtherJobTitles, YesNoEnum } from "@/dto/marketing";

const hubspotLeadCreationURL = `https://api.hsforms.com/submissions/v3/integration/submit/39674306/12cc5240-d11a-49aa-b4d9-0f63b72eced5`;

const fetchWithTimeout = async (url: RequestInfo, options?: RequestInit, timeout = 5000): Promise<Response> => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    return response;
  } catch (error) {
    // Use our own tracker (which is wrapped in try/catch and degrades
    // gracefully when each global is missing). The previous version
    // called window.analytics.track which is undefined in this React app
    // and would throw a TypeError, masking the original fetch error.
    nativePosthogTracking("lead_fetch_error", {
      error_message: error instanceof Error ? error.message : String(error),
      error_name: error instanceof Error ? error.name : "unknown"
    });
    throw error;
  } finally {
    clearTimeout(id);
  }
};

const nativePosthogTracking = (
  name: string,
  properties?: Record<string, string | boolean | number>,
  $set?: Record<string, string | undefined | boolean | number>
) => {
  try {
    window.posthog?.capture?.(name, {
      ...properties,
      $set
    });
  } catch {
    /* posthog not loaded */
  }

  try {
    window.dataLayer?.push({
      ...$set
    });
  } catch {
    /* GTM not loaded */
  }

  try {
    window?.gtag?.("event", name, properties);
  } catch {
    /* gtag not loaded */
  }

  try {
    (
      window as unknown as { newrelic?: { addPageAction?: (n: string, p?: Record<string, unknown>) => void } }
    ).newrelic?.addPageAction?.(name, properties);
  } catch {
    /* NewRelic browser agent not loaded */
  }
};

const SALESFORCE_UUID_PATTERN = /^[a-zA-Z0-9]{15,18}$/;

type SalesforceUuidWindow = Window & { __salesforceUuid?: string };

export const getSalesforceUuid = (): string | null => {
  if (typeof window === "undefined") return null;
  const w = window as SalesforceUuidWindow;
  if (w.__salesforceUuid && SALESFORCE_UUID_PATTERN.test(w.__salesforceUuid)) return w.__salesforceUuid;
  const fromUrl = new URLSearchParams(window.location.search).get("salesforce_uuid");
  if (fromUrl) {
    const trimmed = fromUrl.trim();
    if (SALESFORCE_UUID_PATTERN.test(trimmed)) {
      w.__salesforceUuid = trimmed;
      return trimmed;
    }
  }
  return null;
};

export const parseObj = (str?: string) => {
  try {
    return JSON.parse(str || "null");
  } catch (e) {
    return null;
  }
};

const decodeURIComponentIteratively = (str: string, maxTries = 4): string => {
  for (let i = 0; i < maxTries; i++) {
    try {
      const decoded = decodeURIComponent(str);
      if (decoded === str) {
        return decoded;
      }
      str = decoded;
    } catch {
      return str;
    }
  }
  return str;
};

const readCookie = (): Record<string, string> => {
  const str = decodeURIComponent(document.cookie);
  if (!str?.trim()) return {};
  if (!str.includes("=")) return {};
  return (
    str
      .split(";")
      // Split on the FIRST `=` only, so cookie values containing `=` (e.g. base64
      // payloads with trailing `==`) are preserved verbatim. The previous
      // `v.split("=")` truncated such values and silently broke UTM attribution.
      .map((v) => {
        const trimmed = v.trim();
        const eqIdx = trimmed.indexOf("=");
        if (eqIdx === -1) return [trimmed, ""];
        return [trimmed.slice(0, eqIdx), trimmed.slice(eqIdx + 1)];
      })
      .reduce((acc, v) => {
        acc[decodeURIComponentIteratively(v[0])] = decodeURIComponentIteratively(v[1] ?? "");
        return acc;
      }, {})
  );
};

export const getDLMCCookie = (): DLMCCookie => parseObj(readCookie()?.dlmc) || {};

export const getUrlParams = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search);
  const result: Record<string, string> = {};

  for (const [key, value] of params.entries()) {
    result[key] = value;
  }

  return result;
};

const convertToCalendlyBody = (leadDto: DoorLoopLeadDto): CalendlyPayload => {
  const cookieData = getDLMCCookie();
  const [firstname, ...lastNameParts] = leadDto.name?.split(" ") || [];
  return {
    email: leadDto.email!,
    phone: leadDto.phone!,
    firstname,
    lastname: lastNameParts.join(" "),
    expected_number_of_units__c: leadDto.numberOfUnits!,
    custom_lead_score: calculateLeadScore(leadDto, cookieData),
    user_selected_occupation: leadDto.selectedJobTitle!,
    are_you_a_tenant__attorney__or_accountant_: leadDto.jobTitle!,
    primary_portfolio__type: leadDto.mainPropertyType!,
    customer_reported_timeline__c: leadDto.whenPlanToUseSoftware!,
    manage_properties_us_canada: leadDto.doYouManagePropertiesInTheUSOrCanada!,
    utm_source: cookieData.utmSource || "",
    utm_campaign: cookieData.utmCampaign || "",
    utm_content: cookieData.utmContent || "",
    utm_source___last_touch: cookieData.utmSourceLast || "",
    utm_campaign___last_touch: cookieData.utmCampaignLast || "",
    utm_content___last_touch: cookieData.utmContentLast || "",
    has_filled_out_hubspot_demo_form: true,
    ip_address_country__c: leadDto.country || "",
    leadsource: leadDto.leadSource!,
    blocked_from_demos: "",
    hs_context: "",
    url_params: {
      hide_gdpr_banner: 1,
      hide_event_type_details: 1
    },
    form_labels: {
      email: "Email",
      user_selected_occupation: "What is your role?",
      primary_portfolio__type: "What is your main property type?",
      customer_reported_timeline__c: "When do you plan on using the software?",
      custom_lead_score: "custom lead score",
      utm_source: "utm source",
      utm_source___last_touch: "utm source   last touch",
      utm_campaign: "utm campaign",
      utm_campaign___last_touch: "utm campaign   last touch",
      utm_content___last_touch: "utm content   last touch",
      utm_content: "utm content",
      has_filled_out_hubspot_demo_form: "has filled out hubspot demo form",
      ip_address_country__c: "ip address country  c",
      manage_properties_us_canada: "Do you manage properties in the US or Canada?",
      leadsource: "leadsource",
      blocked_from_demos: "blocked from demos",
      hs_context: "hs context"
    }
  };
};

const CALENDLY_URLS_BY_UNITS = {
  "1-10": "https://calendly.com/doorloop/demo",
  "11-20": "https://calendly.com/doorloop/demo-1",
  "21-50": "https://calendly.com/doorloop/demo-2",
  "51-100": "https://calendly.com/doorloop/demo-4",
  "101-150": "https://calendly.com/doorloop/demo-5",
  "151-300": "https://calendly.com/doorloop/demo-6",
  "300+": "https://calendly.com/doorloop/demo-3"
} as const;

const getCalendlyUrlByUnits = (numberOfUnits?: number): string => {
  if (numberOfUnits == null || numberOfUnits < 1) {
    return CALENDLY_URLS_BY_UNITS["1-10"];
  }
  if (numberOfUnits <= 10) {
    return CALENDLY_URLS_BY_UNITS["1-10"];
  }
  if (numberOfUnits <= 20) {
    return CALENDLY_URLS_BY_UNITS["11-20"];
  }
  if (numberOfUnits <= 50) {
    return CALENDLY_URLS_BY_UNITS["21-50"];
  }
  if (numberOfUnits <= 100) {
    return CALENDLY_URLS_BY_UNITS["51-100"];
  }
  if (numberOfUnits <= 150) {
    return CALENDLY_URLS_BY_UNITS["101-150"];
  }
  if (numberOfUnits <= 300) {
    return CALENDLY_URLS_BY_UNITS["151-300"];
  }
  return CALENDLY_URLS_BY_UNITS["300+"];
};

const appendCalendlyParams = (url: string, leadDto: DoorLoopLeadDto): string => {
  const cookieData = getDLMCCookie();
  const baseParams: Record<string, string> = {
    name: leadDto.name ?? "",
    email: leadDto.email ?? "",
    utm_source: leadDto.utm_source ?? cookieData.utmSourceLast ?? "",
    utm_campaign: leadDto.utm_campaign ?? cookieData.utmCampaignLast ?? "",
    utm_content: leadDto.utm_content ?? cookieData.utmContentLast ?? "",
    utm_medium: leadDto.utm_medium ?? cookieData.utmMediumLast ?? "",
    utm_term: leadDto.utm_term ?? cookieData.utmTermLast ?? "",
    hide_gdpr_banner: "1"
  };
  const sfUuid = getSalesforceUuid();
  if (sfUuid) {
    baseParams.salesforce_uuid = sfUuid;
  }
  const params = new URLSearchParams(baseParams).toString();
  return `${url}${url.includes("?") ? "&" : "?"}${params}`;
};

export const determineCalendlyTarget = (leadDto: DoorLoopLeadDto): CalendlyResult => {
  const cookieData = getDLMCCookie();
  const leadScore = calculateLeadScore(leadDto, cookieData);
  const {
    selectedJobTitle,
    jobTitle,
    mainPropertyType,
    country,
    numberOfUnits,
    leadSource,
    doYouManagePropertiesInTheUSOrCanada
  } = leadDto;

  if (selectedJobTitle === JobTitlesMarketing.OTHER && jobTitle === OtherJobTitles.TENANT) {
    return { isThanksPage: true, type: "arbitrary_url", url: "https://www.doorloop.com/thanks-role" };
  }
  if ([MainPropertyTypeEnum.STORAGE, MainPropertyTypeEnum.SHORT_TERM_RENTALS].includes(mainPropertyType!)) {
    return { isThanksPage: true, type: "arbitrary_url", url: "https://www.doorloop.com/thanks-portfolio" };
  }

  if (leadScore < 33) {
    return { isThanksPage: true, type: "arbitrary_url", url: "https://www.doorloop.com/thanks-email" };
  }
  const restrictedCountries = ["Cuba", "Belarus", "Iran", "North Korea", "Russia", "Ukraine", "Syria", "Venezuela"];
  if (
    country &&
    (restrictedCountries.includes(country) ||
      (!country.toLowerCase().includes("united states") &&
        !country.toLowerCase().includes("canada") &&
        doYouManagePropertiesInTheUSOrCanada === YesNoEnum.NO))
  ) {
    return { isThanksPage: true, type: "arbitrary_url", url: "https://www.doorloop.com/thanks-country" };
  }

  if (cookieData.landingPage?.toLowerCase().includes("squatter") || leadSource?.toLowerCase() === "scheduled call") {
    return { type: "event_type_id", url: "https://calendly.com/doorloop/doorloop-call-clone-1" };
  }

  const isHOA =
    mainPropertyType === MainPropertyTypeEnum.COMMUNITY_ASSOCIATIONS ||
    selectedJobTitle === JobTitlesMarketing.HOA_MEMBER_OR_MANAGER;

  if (isHOA && numberOfUnits) {
    if (numberOfUnits < 10)
      return { type: "event_type_id", url: "https://calendly.com/doorloop/doorloop-hoa-call-clone-1" };
    if (numberOfUnits < 300)
      return { type: "event_type_id", url: "https://calendly.com/doorloop/doorloop-hoa-call-clone-2" };
    return { type: "event_type_id", url: "https://calendly.com/doorloop/doorloop-hoa-call-mm-clone" };
  }

  return { type: "event_type_id", url: getCalendlyUrlByUnits(numberOfUnits) };
};

export const getCalendlyURL = async (leadDto: DoorLoopLeadDto): Promise<CalendlyCallResponse> => {
  const { type, url, isThanksPage } = determineCalendlyTarget(leadDto);
  return {
    result_type: type,
    result_url: isThanksPage ? url : appendCalendlyParams(url, leadDto)
  };
};

const getHubspotCookie = (): string | null => {
  const name = "hubspotutk";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

export const createHubSpotObject = (dto: DoorLoopLeadDto): HubSpotObject => {
  const cookieData = getDLMCCookie();
  const urlParams = getUrlParams();

  const {
    ipAddress,
    country,
    isUsingVPN,
    email,
    name,
    phone,
    numberOfUnits,
    selectedJobTitle,
    whenPlanToUseSoftware,
    mainPropertyType,
    jobTitle,
    doYouManagePropertiesInTheUSOrCanada,
    utm_source,
    utm_campaign,
    utm_term,
    utm_content,
    utm_medium,
    leadSource
  } = dto;

  const data: HubSpotObject = {
    fields: [],
    context: {
      hutk: getHubspotCookie(),
      pageUri: window.location.href,
      pageName: document.title
    }
  };

  if (ipAddress) {
    data.context.ipAddress = ipAddress;
  }

  const push = (name: string, value: string | undefined) => {
    value &&
      data.fields.push({
        name,
        value
      });
  };

  push("ip_address__c", ipAddress);
  push("ip_address_country__c", country);
  push("using_vpn_", isUsingVPN ? "true" : "false");
  push("ab_test", "05-2026-two-step-form");
  push("phone", phone);
  push("expected_number_of_units__c", numberOfUnits?.toString());

  if (name) {
    const [firstName, ...lastNameParts] = name.split(" ");
    const lastName = lastNameParts.join(" ");
    push("full_name", name);
    push("firstname", firstName);
    push("lastname", lastName);
  }

  push("primary_portfolio__type", mainPropertyType);
  push("user_selected_occupation", selectedJobTitle);
  push("customer_reported_timeline__c", whenPlanToUseSoftware);
  push("are_you_a_tenant__attorney__or_accountant_", jobTitle);
  push("manage_properties_us_canada", doYouManagePropertiesInTheUSOrCanada);

  push("email", email || "");
  push("leadsource", leadSource);
  push("has_filled_out_hubspot_demo_form", "true");

  push("utm_source", utm_source || cookieData?.utmSource);
  push("utm_campaign", utm_campaign || cookieData?.utmCampaign);
  push("utm_content", utm_content || cookieData?.utmContent);
  push("utm_term", utm_term || cookieData?.utmTerm);
  push("utm_medium", utm_medium || cookieData?.utmMedium);

  if (cookieData) {
    push("utm_campaign___all_touches", cookieData.utmCampaignsAll);
    push("utm_campaign___last_touch", cookieData.utmCampaignLast);

    push("utm_content___all_touches", cookieData.utmContentsAll);
    push("utm_content___last_touch", cookieData.utmContentLast);

    push("utm_medium___all_touches", cookieData.utmMediumsAll);
    push("utm_medium___last_touch", cookieData.utmMediumLast);

    push("utm_source___last_touch", cookieData.utmSourceLast);
    push("utm_sources___all", cookieData.utmSourcesAll);

    push("utm_term___all_touches", cookieData.utmTermsAll);
    push("utm_term___last_touch", cookieData.utmTermLast);

    push("adwords_gclid", cookieData.adwordsGclid);
    push("msclkid", cookieData.msClkid);
    push("li_fat_id", cookieData.linkedinId);

    push("adgroup_id", cookieData.adgroupID);
    push("keyword_id", cookieData.keywordID);
    push("campaign_id", cookieData.campaignID);

    push("adgroup_id_all_touches", cookieData.adgroupIdAll);
    push("keyword_id_all_touches", cookieData.keywordIdAll);
    push("campaign_id_all_touches", cookieData.campaignIdAll);

    push("referrer", cookieData.referrer);
    push("first_website_visit_at", cookieData.firstWebsiteVisitAt);
    push("landing_page", cookieData.landingPage);
    push("ttclid", cookieData.ttclid);

    push("irclickid", cookieData.irclickid);
    push("irpid", cookieData.irpid);
    push("sharedid", cookieData.sharedid);
    push("iradname", cookieData.iradname);
  }

  push("custom_lead_score", calculateLeadScore(dto, cookieData).toString());

  return data;
};
const reportToHubSpot = async (dto: DoorLoopLeadDto, hubspotFormId: string): Promise<boolean> => {
  // Previously fire-and-forget with no error handling. A 4xx/5xx response or
  // a network failure (offline, DNS, CORS) silently dropped the lead. Now
  // awaited internally with explicit success/failure tracking — callers can
  // still treat it as fire-and-forget by not awaiting the promise.
  try {
    const response = await fetchWithTimeout(
      `https://api.hsforms.com/submissions/v3/integration/submit/39674306/${hubspotFormId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(createHubSpotObject(dto))
      },
      10_000
    );
    if (!response.ok) {
      let errorBody: unknown;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { raw: "<non-json response>" };
      }
      nativePosthogTracking("hubspot_form_submit_failed", {
        form_id: hubspotFormId,
        status: response.status,
        error_type: "http_error",
        error_body: typeof errorBody === "string" ? errorBody : JSON.stringify(errorBody).slice(0, 500)
      });
      return false;
    }
    nativePosthogTracking("hubspot_form_submit_success", { form_id: hubspotFormId });
    return true;
  } catch (error) {
    nativePosthogTracking("hubspot_form_submit_failed", {
      form_id: hubspotFormId,
      error_type: error instanceof Error && error.name === "AbortError" ? "timeout" : "network_error",
      error_message: error instanceof Error ? error.message : String(error)
    });
    return false;
  }
};

const makeEmailedTracker =
  (email?: string) => (event: string, properties?: Record<string, string | boolean | number>) =>
    nativePosthogTracking(event, properties, { email });

const validateEmailFormat = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const validateEmail = memoize(
  async (
    email?: string
  ): Promise<{
    isValid: boolean;
    result: string;
  }> => {
    // Dev-only escape hatch for local work without the marketing-server
    // backend running. Requires BOTH: (1) running in development mode and
    // (2) explicit `?skipEmailValidation=1` opt-in via URL query.
    // The previous version short-circuited on an exact `href` match against
    // `http://localhost:3000/demo/additional-info`, which silently bypassed
    // validation for ALL local devs and broke any test or URL with a query
    // string, plus made it impossible to exercise the validation path
    // locally. This new gate is explicit and never fires in production.
    if (
      import.meta.env.MODE === "development" &&
      new URLSearchParams(window.location.search).get("skipEmailValidation") === "1"
    ) {
      return {
        isValid: true,
        result: "valid"
      };
    }
    const track = makeEmailedTracker(email);

    if (!email || !validateEmailFormat(email)) {
      track("lead_email_invalid");
      return {
        isValid: false,
        result: "invalid_email"
      };
    }

    const validationUrl = `${import.meta.env.VITE_BACKEND_URL}/email-validation/validate`;
    try {
      const response = await fetchWithTimeout(
        validationUrl,
        {
          method: "POST",
          body: JSON.stringify({ email }),
          headers: { "Content-Type": "application/json" }
        },
        10_000
      );

      if (response.status === 400) {
        track("lead_email_invalid");
        return {
          isValid: false,
          result: "invalid_email"
        };
      }

      if (response.status === 429) {
        track("lead_rate_limited");
        return {
          isValid: false,
          result: "too_many_requests"
        };
      }

      if (!response.ok) {
        track("neverbounce_api_failure", {
          status: response.status,
          email: email,
          error_type: "http_error",
          fallback_used: true
        });

        return {
          isValid: true,
          result: "fallback_valid"
        };
      }

      const data: NeverbounceResponse = await response.json();

      if (!data || typeof data.result !== "string" || data.status !== "success") {
        track("neverbounce_api_failure", {
          email: email,
          error_type: "invalid_response_structure",
          response_data: JSON.stringify(data),
          fallback_used: true
        });

        return {
          isValid: true,
          result: "fallback_valid"
        };
      }

      if (data.result === "invalid" || data.result === "disposable") {
        track("lead_email_invalid");
        return {
          isValid: false,
          result: "invalid_email"
        };
      }

      track("lead_valid_email");
      return {
        isValid: ["valid", "catchall", "unknown"].includes(data.result),
        result: data.result
      };
    } catch (error) {
      track("lead_email_error", {
        email: email,
        error_type: "network_error",
        error_message: error instanceof Error ? error.message : String(error),
        fallback_used: true
      });

      return {
        isValid: true,
        result: "fallback_valid"
      };
    }
  }
);

export type EmailLeadResult =
  | { ok: true; result: string }
  | { ok: false; reason: "invalid_email" | "rate_limited"; detail: string }
  | { ok: false; reason: "network_error"; detail: string };

const createEmailOnlyLead = async (dto: DoorLoopLeadDto): Promise<EmailLeadResult> => {
  const email = dto.email || "";
  const track = makeEmailedTracker(email);
  const { isValid, result: validationResult } = await validateEmail(email);
  if (!isValid) {
    if (validationResult === "too_many_requests") {
      return { ok: false, reason: "rate_limited", detail: validationResult };
    }
    return { ok: false, reason: "invalid_email", detail: validationResult };
  }

  const data = createHubSpotObject(dto);
  data.fields.push({
    name: "neverbouncevalidationresult",
    value: validationResult
  });

  try {
    track("lead_attempted_form_api_call");
    const response = await fetchWithTimeout(
      hubspotLeadCreationURL,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      },
      10_000
    );

    if (!response.ok) {
      let errors: unknown = "<non-json>";
      try {
        errors = await response.json();
      } catch {
        /* response had no JSON body */
      }
      track("lead_created_error", {
        status: response.status,
        errors_summary: typeof errors === "string" ? errors : JSON.stringify(errors).slice(0, 500)
      });
      // Server-side rejection of the email-only lead is NOT the same as
      // an invalid email. Surface as network_error so the caller does
      // not falsely flag the user's email field.
      return { ok: false, reason: "network_error", detail: `http_${response.status}` };
    }
    return { ok: true, result: validationResult };
  } catch (error: unknown) {
    track("lead_created_error", {
      error_message: error instanceof Error ? error.message : String(error),
      error_name: error instanceof Error ? error.name : "unknown"
    });
    return {
      ok: false,
      reason: "network_error",
      detail: error instanceof Error ? error.message : String(error)
    };
  }
};

export const demoFormUtils = {
  getCalendlyURL,
  validateEmail,
  hubspot: {
    createEmailOnlyLead,
    reportToHubSpot
  },
  track: nativePosthogTracking
};
